import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { APIProvider, Map, InfoWindow } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { addOpacityToHexColor, capitalize } from '../../common/utils/common.util';
import { HEIGHTS } from '../report/constants';
import { toast } from 'react-toastify';
import './maps-styles.css';
import { getClusterAlgorithm, offsetDuplicateCoordinates } from './map.util';
import { getColorByArea } from '../project/project.util';
import { INFO_WINDOW_TIMEOUT } from '../../common/constants/maps';
import { ROUTES } from '../../common/constants/urls';
import { Link } from 'react-router-dom';
import { Launch } from '@mui/icons-material';

interface CoordinateWithData {
  lat: number;
  lng: number;
  comp: any;
}

interface MapWithPinsProps {
  selectedComps: any[] | undefined;
  updateComps?: (updatedComps: any[]) => void;
  isFromCAReport: boolean;
}

const containerStyle = {
  width: '100%',
  height: HEIGHTS.MAX_TABLE_HEIGHT + HEIGHTS.SUBTOTAL_HEIGHT,
};

const ComparablesMap: React.FC<MapWithPinsProps> = ({ selectedComps, updateComps, isFromCAReport }) => {
  const [hoveredComp, setHoveredComp] = useState<any | null>(null);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [hasSetBounds, setHasSetBounds] = useState(false);
  const [isInfoWindowHovered, setIsInfoWindowHovered] = useState(false); // Track InfoWindow hover state

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseOverMarker = (project: any) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredComp(project);
  };

  const handleMouseOutMarker = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (!isInfoWindowHovered) {
        setHoveredComp(null);
      }
    }, INFO_WINDOW_TIMEOUT);
  };

  const handleMouseOverInfoWindow = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsInfoWindowHovered(true);
  };

  const handleMouseOutInfoWindow = () => {
    timeoutRef.current = setTimeout(() => {
      setIsInfoWindowHovered(false);
      setHoveredComp(null);
    }, INFO_WINDOW_TIMEOUT);
  };

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '';

  const handleIncludedCompsChange = useCallback(
    (compId: string, value: boolean) => {
      const updatedComps = selectedComps?.map(comp => {
        if (comp.comp.id === compId) {
          return { ...comp, isIncluded: value };
        }
        return comp;
      });
      const includedCompsAfterUpdate = updatedComps?.filter(comp => comp.isIncluded);
      if (includedCompsAfterUpdate?.length === 0) {
        toast.error('At least one comparable must be selected');
        return;
      }
      if (updateComps) {
        updateComps(updatedComps || []);
      }
    },
    [selectedComps, updateComps],
  );

  const validCoordinates = useMemo(() => {
    const rawCoordinates: CoordinateWithData[] = (selectedComps || [])
      .filter(comp => {
        const projectAddress = isFromCAReport ? comp.project?.projectAddress : comp?.comp?.projectAddress;
        return (
          projectAddress?.latitude !== null &&
          projectAddress?.latitude !== undefined &&
          projectAddress?.longitude !== null &&
          projectAddress?.longitude !== undefined
        );
      })
      .map(comp => {
        const projectAddress = isFromCAReport ? comp.project.projectAddress : comp.comp.projectAddress;
        const lat = Number(projectAddress.latitude);
        const lng = Number(projectAddress.longitude);
        return { lat, lng, comp };
      });

    return offsetDuplicateCoordinates(rawCoordinates);
  }, [selectedComps, isFromCAReport]);

  // Initialize MarkerClusterer when map is ready
  useEffect(() => {
    if (!map || !isMapReady || clusterer) return;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {
      const algorithm = getClusterAlgorithm();

      const newClusterer = new MarkerClusterer({
        map,
        algorithm,
        renderer: {
          render: ({ count, position }) => {
            const marker = new google.maps.marker.AdvancedMarkerElement({
              position,
              content: createClusterBubble(count),
            });
            return marker;
          },
        },
      });

      setClusterer(newClusterer);
    };
    overlay.setMap(map);
  }, [map, isMapReady, clusterer]);

  const createClusterBubble = (count: number) => {
    const minCount = 1;
    const maxCount = validCoordinates.length || 200;
    const minSize = 30;
    const maxSize = 100;

    const size = minSize + ((Math.min(count, maxCount) - minCount) / (maxCount - minCount)) * (maxSize - minSize);

    const div = document.createElement('div');
    div.style.background = 'rgba(63, 81, 181, 0.5)';
    div.style.color = '#ffffff';
    div.style.border = '2px solid rgba(63, 81, 181)';
    div.style.borderRadius = '50%';
    div.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
    div.style.width = `${size}px`;
    div.style.height = `${size}px`;
    div.style.display = 'flex';
    div.style.justifyContent = 'center';
    div.style.alignItems = 'center';
    div.style.fontSize = `${Math.min(12 + count * 0.2, 24)}px`;
    div.textContent = `${count}`;
    return div;
  };

  // Update markers when validCoordinates or dependencies change
  useEffect(() => {
    const addAdvancedMarkersToClusterer = async () => {
      if (!clusterer || !map || !isMapReady) return;

      clusterer.clearMarkers();

      const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary('marker')) as {
        AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
        PinElement: typeof google.maps.marker.PinElement;
      };

      const markers = validCoordinates.map(coord => {
        const area = coord.comp?.comp?.projectAddress?.customArea;
        const color = getColorByArea(area);

        const pin = new PinElement({
          background: coord.comp.isIncluded || isFromCAReport ? `${addOpacityToHexColor(color, 80)}` : '#bdbdbd',
          borderColor: '#FFFFFF',
          glyphColor: color,
          scale: 1.2,
        });

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: coord.lat, lng: coord.lng },
          content: pin.element,
          gmpClickable: true,
        });

        // Hover listeners
        marker.element?.addEventListener('mouseenter', () => handleMouseOverMarker(coord));
        marker.element?.addEventListener('mouseleave', handleMouseOutMarker);

        // Click listener for include/exclude behavior
        marker.element?.addEventListener('click', () => {
          if (!isFromCAReport) {
            handleIncludedCompsChange(coord.comp.comp.id, !coord.comp.isIncluded);
          }
        });

        return marker;
      });

      clusterer.addMarkers(markers);
    };

    addAdvancedMarkersToClusterer();
  }, [clusterer, validCoordinates, isFromCAReport, handleIncludedCompsChange, map, isMapReady]);

  if (!googleMapsApiKey) {
    return <p>Google Maps API key is missing.</p>;
  }

  if (validCoordinates.length === 0) {
    return <p>No valid address coordinates found for selected projects.</p>;
  }
  console.log(hoveredComp);
  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <div style={containerStyle}>
        <Map
          mapId={mapId}
          style={{ width: '100%', height: '100%' }}
          onIdle={event => {
            const mapInstance = event.map;
            setMap(mapInstance);
            setIsMapReady(true);

            if (!hasSetBounds && validCoordinates.length > 0) {
              const bounds = new google.maps.LatLngBounds();
              validCoordinates.forEach(coord => bounds.extend({ lat: coord.lat, lng: coord.lng }));
              mapInstance.fitBounds(bounds);
              setHasSetBounds(true); // Ensure bounds are set only once
            }
          }}
        >
          {hoveredComp && (
            <InfoWindow
              position={{
                lat: hoveredComp.lat,
                lng: hoveredComp.lng,
              }}
              onCloseClick={() => setHoveredComp(null)}
            >
              <div
                style={{ minWidth: '100px', textAlign: 'center' }}
                onMouseOver={handleMouseOverInfoWindow}
                onMouseOut={handleMouseOutInfoWindow}
              >
                <Link
                  to={isFromCAReport ? `${ROUTES.COMPS}/${hoveredComp?.comp?.id}` : `${ROUTES.PROJECTS}/${hoveredComp?.comp?.comp?.id}`}
                  target='_blank'
                  style={{ display: 'flex', alignItems: 'center', width: '100%', color: 'unset' }}
                >
                  <p style={{ margin: '0', fontWeight: 'bold' }}>
                    {isFromCAReport
                      ? hoveredComp.comp.project?.projectNumber && hoveredComp.comp.project?.projectTitle
                        ? `${hoveredComp.comp.project?.projectNumber} - ${hoveredComp.comp.project?.projectTitle}`
                        : 'No Title Available'
                      : hoveredComp.comp.comp?.projectNumber && hoveredComp.comp.comp?.projectTitle
                        ? `${hoveredComp.comp.comp?.projectNumber} - ${hoveredComp.comp.comp?.projectTitle}`
                        : 'No Title Available'}
                  </p>
                  <Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />
                </Link>
                <p style={{ margin: '0', fontSize: '12px', color: '#555' }}>
                  {isFromCAReport
                    ? hoveredComp.comp.project?.projectAddress?.city && capitalize(hoveredComp.comp.project?.projectAddress?.city?.name)
                    : hoveredComp.comp?.comp?.projectAddress?.city && capitalize(hoveredComp.comp?.comp.projectAddress.city)}
                  {isFromCAReport
                    ? hoveredComp.comp.project?.projectAddress?.city?.state &&
                      `, ${hoveredComp.comp.project.projectAddress.city?.state.stateCode?.toUpperCase()}`
                    : hoveredComp.comp?.comp?.projectAddress?.state && `, ${hoveredComp.comp?.comp.projectAddress.state.toUpperCase()}`}
                </p>
                <p style={{ margin: '0', fontSize: '12px', color: '#555' }}>
                  {`Project Type: ${
                    isFromCAReport ? hoveredComp.comp.project?.projectTypeDescription || '' : hoveredComp.comp?.comp?.projectType || ''
                  }`}
                </p>
                <p style={{ margin: '0', fontSize: '12px', color: '#555' }}>
                  Observer:{' '}
                  {isFromCAReport
                    ? hoveredComp.comp.project?.observer
                      ? `${hoveredComp.comp.project.observer.firstName} ${hoveredComp.comp.project.observer.lastName}`
                      : ''
                    : hoveredComp.comp?.comp?.observer
                      ? `${hoveredComp.comp.comp.observer.firstName} ${hoveredComp.comp.comp.observer.lastName}`
                      : ''}
                </p>
              </div>
            </InfoWindow>
          )}
        </Map>
      </div>
    </APIProvider>
  );
};

export default ComparablesMap;
